import { useMemo, useState } from "react";
import type { IntegratedSorting } from "@devexpress/dx-react-grid";
import { sequenceS } from "fp-ts/lib/Apply";
import type { Ordering } from "fp-ts/lib/Ordering";
import { useStableMemo } from "fp-ts-react-stable-hooks";
import type { Store } from "redux";

import { b, O, Ord, pipe, RA, s } from "@scripts/fp-ts";
import { useConfig } from "@scripts/react/context/Config";

import { tableSearchFilterFn } from "../../filters/filterFns";
import type { Searchable } from "../Table";
import { createColumnSortList, defaultSortState, type ExpandedRowIds, formatCustomSortValueExtension, type SortBy, type SortByVal, type TableColumnRow, type TableRowModel } from "../tableSyntax";

export const useTableData = <SB extends SortBy<keyof A>, A, MetaData, ExtraColumns extends string[]>(
  columns: TableColumnRow<A, MetaData, ExtraColumns>["Columns"],
  rows: ReadonlyArray<TableColumnRow<A, MetaData, ExtraColumns>["Row"]>,
  searchable: O.Option<Searchable>,
  sortable: O.Option<SB>,
  store: O.Option<Store>
) => {
  const config = useConfig();

  const isSearchable = O.isSome(searchable);
  const isSortable = O.isSome(sortable);

  const [expandedRowIds, setExpandedRowIds] = useState<ExpandedRowIds>([]);

  const sortByVal: SortByVal<SB, A> = useStableMemo(
    () => ({ sortBy: O.toUndefined(sortable) }),
    [sortable], RA.getEq(O.getEq(s.Eq))
  );

  const columnSortList = useMemo(() =>
    isSortable ? createColumnSortList<A, MetaData, SB, ExtraColumns>(columns, sortByVal.sortBy) : [],
    [columns, isSortable, sortByVal.sortBy]);

  const [searchVal, ignoreSearch] = pipe(
    searchable,
    O.fold(
      () => ["", false],
      ([, v, iS]) => [v, iS ?? false]
    )
  );

  const searchedRows = useMemo(() => isSearchable && !ignoreSearch && searchVal.length > 0
    ? tableSearchFilterFn(config, columns, O.toUndefined(store), expandedRowIds)(searchVal)(rows)
    : rows,
    [columns, config, expandedRowIds, ignoreSearch, isSearchable, rows, searchVal, store]
  );

  const sortColumnExtensions = useMemo<IntegratedSorting.ColumnExtension[]>(() =>
    pipe(isSortable, b.fold(() => [], () => formatCustomSortValueExtension(columns))
    ), [columns, isSortable]);

  const [sortColumnO, sortDirectionO] = pipe(
    sortByVal.sortBy,
    O.fromNullable,
    O.fold(
      () => ["", ""],
      _ => _.split("-")
    ),
    _ => [O.fromNullable(_[0]), O.fromNullable(_[1])]
  );

  const columnCompareO = pipe(
    sortColumnO,
    O.chain(sortColumn => pipe(
      sortColumnExtensions,
      RA.findFirst(_ => _.columnName === sortColumn),
      O.chain(_ => O.fromNullable(_.compare))
    ))
  );

  const sortedRows = pipe(
    sequenceS(O.Apply)({
      columnCompare: columnCompareO,
      sortColumn: sortColumnO,
      sortDirection: sortDirectionO,
    }),
    O.fold(
      () => searchedRows,
      ({ columnCompare, sortColumn, sortDirection }) => {
        const ord = Ord.contramap(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
          (x: TableRowModel<A, MetaData>) => (x as any)[sortColumn]
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
        )(Ord.fromCompare(columnCompare as (first: any, second: any) => Ordering));

        return pipe(
          searchedRows,
          RA.sort(sortDirection === "desc" ? Ord.reverse(ord) : ord)
        );
      })
  );

  const sorting = defaultSortState<SB, A>(columnSortList, sortByVal.sortBy);

  return {
    columnSortList,
    expandedRowIds,
    rows: sortedRows,
    searchVal,
    searchable,
    setExpandedRowIds,
    sortByVal,
    sortable,
    sorting,
    store,
    columns,
  };
};

export type TableData<SB extends SortBy<keyof A>, A, MetaData, ExtraColumns extends string[]> =
  ReturnType<typeof useTableData<SB, A, MetaData, ExtraColumns>>;
